import type { Middleware } from '@reduxjs/toolkit'

import { CourseProgressTypeEnum, SuspendDataType } from 'gql/__generated__/graphql'
import { isAction } from 'services/Store/Project/actionTypeGuard'
import { setCourseNavigation } from 'services/Store/Project/actions'
import type { ProjectActionsTypes } from 'services/Store/Project/reducer'
import {
  getBlockId,
  getProgress,
  getScormSettings,
  getSection,
} from 'services/Store/Project/selectors'
import { stateToSuspendData } from 'services/Store/Project/suspendData'
import type { IProjectContext } from 'services/Store/Project/types'

import { LmsService } from '../lms.service'
import * as handlers from './lms.handlers'
import { getCommonVariables, type IHandlerContext } from './lms.utils'

export const NAVIGATE_ACTIONS: ProjectActionsTypes[keyof ProjectActionsTypes][] = [
  'project/setCourseNavigation',
  'project/courseStart',
  'project/courseContinue',
  'project/testStart',
  'project/testRestart',
  'project/testEnd',
]

// Module-level guard to prevent duplicate window listeners on HMR / multiple
// middleware instantiations. The store reference itself is never hoisted —
// only captured inside the factory closure.
let navigateToListenerRegistered = false
let navigateToHandlerRef: ((event: MessageEvent) => void) | null = null

interface INavigateToMessage {
  type: 'navigate_to'
  sectionId?: string | null
  blockId?: string | null
}

const isNavigateToMessage = (data: unknown): data is INavigateToMessage =>
  typeof data === 'object' && data !== null && (data as { type?: unknown }).type === 'navigate_to'

export const lmsMiddleware: Middleware = (store) => {
  let navState = handlers.createNavState()

  // Phase B.2: listen for portal → launcher → inner course `navigate_to`
  // postMessage. Dispatches `setCourseNavigation` so the course updates
  // selection without going through the SCORM cmi.location round-trip.
  if (!navigateToListenerRegistered && typeof window !== 'undefined') {
    const rawLmsOrigin = new URLSearchParams(window.location.search).get('lmsOrigin')

    // `event.origin` is always canonical (no trailing slash). Normalize the
    // query-param value via `new URL(...).origin` so a launcher passing
    // `https://launcher.test/` still matches `https://launcher.test`.
    let lmsOrigin: string | null = null
    if (rawLmsOrigin) {
      try {
        lmsOrigin = new URL(rawLmsOrigin).origin
      } catch {
        // Malformed query param — skip listener registration (same branch as
        // "no query param": nothing to validate against).
      }
    }

    if (lmsOrigin) {
      navigateToListenerRegistered = true
      navigateToHandlerRef = (event) => {
        // Course iframe lives inside the launcher — parent must be the launcher.
        if (event.source !== window.parent) {
          return
        }
        // Origin must match the launcher's origin that was passed via query param.
        if (event.origin !== lmsOrigin) {
          return
        }
        if (!isNavigateToMessage(event.data)) {
          return
        }

        store.dispatch(
          setCourseNavigation({
            sectionId: event.data.sectionId ?? null,
            blockId: event.data.blockId ?? null,
          }),
        )
      }
      window.addEventListener('message', navigateToHandlerRef)
    }
  }

  return (next) => async (actionPlain) => {
    const action = actionPlain as {
      type: ProjectActionsTypes[keyof ProjectActionsTypes]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: any
    }
    const result = next(action)

    const state = store.getState().project as IProjectContext
    const lms = LmsService.instance

    if (!lms.isInitialized()) {
      navState = handlers.createNavState()
      return result
    }

    const scormSettings = getScormSettings(state)
    if (!scormSettings || !state.data.project.id) {
      return result
    }

    const section = getSection(state)
    const blockId = getBlockId(state)
    const finalTestId = scormSettings.runtimeSettings?.testSectionId || undefined
    const progressType = scormSettings.runtimeSettings?.progressType
    // Backward-compatible: if progressType is not set but finalTestId exists, treat as test mode
    // This handles old exports that don't have progressType field
    const isTestMode =
      (progressType === CourseProgressTypeEnum.test || progressType === undefined) && !!finalTestId
    const isViewMode = !isTestMode
    const finished = state.state.course.status === 'finished'

    const ctx: IHandlerContext = {
      get state() {
        return store.getState().project as IProjectContext
      },
      lms,
      dispatch: store.dispatch,
      scormSettings,
      commonVars: getCommonVariables(state),
      store,
    }

    let shouldCommit = false

    // Suspend Data (always)
    const suspendData = stateToSuspendData(state)
    lms.setSuspendData(suspendData)

    // === Action Routing ===

    // Custom analytics event
    if (isAction(action, 'sendCourseAnalytics')) {
      for (const stmt of Array.isArray(action.payload) ? action.payload : [action.payload]) {
        await lms.trackEvent(stmt)
      }
    }

    // Course Initialized
    if (!navState.courseInitialized) {
      const courseStartShouldCommit = await handlers.handleInitialized(ctx, navState, isViewMode)
      if (courseStartShouldCommit) {
        shouldCommit = true
      }
    }

    if (isAction(action, 'setElement')) {
      shouldCommit = true
    }

    // Question Validate
    if (blockId && isAction(action, 'questionValidate')) {
      await handlers.handleQuestionValidate(ctx, action.payload, finalTestId)
      shouldCommit = true
    }

    // Test/View Mode handling (only after course is initialized)
    if (!finished && navState.courseInitialized) {
      if (isTestMode && section?.id === finalTestId) {
        const progress = getProgress(state, finalTestId)
        // Only send progress if it changed
        if (progress !== navState.lastProgress) {
          await lms.setProgress(progress)
          navState.lastProgress = progress
        }

        if (isAction(action, 'testEnd')) {
          await handlers.handleTestEnd(ctx, finalTestId)
          shouldCommit = true
        }

        if (isAction(action, 'testRestart')) {
          await handlers.handleTestRestart(ctx, navState)
          shouldCommit = true
        }
      } else if (isViewMode) {
        // Check progress on every action (not just navigation) - progress updates when blocks are viewed
        const progress = getProgress(state)
        const progressShouldCommit = await handlers.handleViewModeProgress(ctx, navState, progress)
        if (progressShouldCommit) {
          shouldCommit = true
        }
      }
    }

    // Reflection
    if (isAction(action, 'sendReflection')) {
      await handlers.handleReflection(ctx, action.payload)
    }

    // Navigation actions
    if (NAVIGATE_ACTIONS.includes(action.type)) {
      await lms.setLocation(location.hash.slice(1))

      if (isAction(action, 'courseStart')) {
        await handlers.handleCourseStart(ctx, navState)
      }

      const currentSection = section
        ? { id: section.id, name: section.name || '', type: section.type }
        : null
      await handlers.handleNavigation(ctx, navState, currentSection)

      if (scormSettings.runtimeSettings?.suspendDataType?.includes(SuspendDataType.navigation)) {
        shouldCommit = true
      }
    }

    // Course End
    if (isAction(action, 'courseEnd')) {
      await handlers.handleCourseEnd(ctx, navState)

      navState.prevSectionId = null
      navState.prevSectionName = null
      navState.prevSectionType = null
      navState.courseInitialized = false

      return result
    }

    if (shouldCommit) {
      await lms.commit()
    }

    return result
  }
}

// Test-only: removes the module-level navigate_to listener and resets the
// registration guard. Called from `afterEach` so listeners don't accumulate
// across tests sharing the same module instance.
export const __resetForTesting = () => {
  if (navigateToHandlerRef && typeof window !== 'undefined') {
    window.removeEventListener('message', navigateToHandlerRef)
  }
  navigateToHandlerRef = null
  navigateToListenerRegistered = false
}
